import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigationType } from 'react-router-dom';

import { getEnterpriseIdFromToken } from '../component/context/EnterpriseConfigContext';

// Snapshot dos filtros de uma tela de listagem, gravado em sessionStorage
// (chaveado por empresa) SOMENTE ao abrir o detalhe de um registro e consumido
// (removido) na montagem seguinte da lista. Restaurado apenas se a entrada na
// lista for um retorno (POP: voltar do detalhe); menu/links (PUSH) e F5 abrem
// vazia. Também deve ser removido no logout (AccountMenu).
//
// - prefix: prefixo da chave (ex.: 'service_quotes_list_filters:').
// - defaults: filtros vazios usados quando não há o que restaurar.
// - sanitize(saved): valida campo a campo o objeto salvo e devolve os filtros.
//
// Retorna { initialFilters, saveSnapshot(obj) }. `initialFilters` é lido uma
// única vez, antes da primeira requisição. O consumo fica em efeito (e não no
// inicializador do state) para o StrictMode não perder o snapshot.

export function snapshotKey(prefix) {
    return `${prefix}${getEnterpriseIdFromToken() || 'anon'}`;
}

// Converte 'YYYY-MM-DD' salvo em dayjs válido (ou null).
export function parseSnapshotDate(value) {
    if (typeof value !== 'string') return null;
    const d = dayjs(value, 'YYYY-MM-DD', true);
    return d.isValid() ? d : null;
}

// Converte dayjs em 'YYYY-MM-DD' (ou null) para gravar no snapshot.
export function formatSnapshotDate(value) {
    return value && value.isValid() ? value.format('YYYY-MM-DD') : null;
}

export default function useListFiltersSnapshot(prefix, defaults, sanitize) {
    const navigationType = useNavigationType();
    const [initialFilters] = useState(() => {
        if (navigationType !== 'POP') return defaults;
        try {
            const raw = sessionStorage.getItem(snapshotKey(prefix));
            if (!raw) return defaults;
            const saved = JSON.parse(raw);
            if (!saved || typeof saved !== 'object') return defaults;
            return sanitize(saved);
        } catch (error) {
            return defaults;
        }
    });

    useEffect(() => {
        try {
            sessionStorage.removeItem(snapshotKey(prefix));
        } catch (error) {
            // sessionStorage indisponível: nada a limpar.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveSnapshot = useCallback((snapshot) => {
        try {
            sessionStorage.setItem(snapshotKey(prefix), JSON.stringify(snapshot));
        } catch (error) {
            // sessionStorage indisponível: apenas não preserva os filtros.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { initialFilters, saveSnapshot };
}
